'use client';

import type { CSSProperties, ReactElement } from 'react';
import { Toaster as SonnerToaster, type ToasterProps as SonnerToasterProps } from 'sonner';

/**
 * sonner's own colour variables pointed at the theme tokens, so every toast follows the light / dark theme. Inline,
 * because sonner's stylesheet is unlayered and would beat a class in `@layer ui`. React's `CSSProperties` has no
 * custom properties, hence the cast.
 */
const THEME_VARIABLES = {
    '--normal-bg': 'var(--color-background)',
    '--normal-bg-hover': 'var(--color-surface-hover)',
    '--normal-border': 'var(--color-border)',
    '--normal-border-hover': 'var(--color-border-hover)',
    '--normal-text': 'var(--color-text)',
    '--success-bg': 'var(--color-success-soft)',
    '--success-border': 'var(--color-success)',
    '--success-text': 'var(--color-text)',
    '--info-bg': 'var(--color-primary-soft)',
    '--info-border': 'var(--color-primary)',
    '--info-text': 'var(--color-text)',
    '--warning-bg': 'var(--color-warning-soft)',
    '--warning-border': 'var(--color-warning)',
    '--warning-text': 'var(--color-text)',
    '--error-bg': 'var(--color-error-soft)',
    '--error-border': 'var(--color-error)',
    '--error-text': 'var(--color-text)',
    '--border-radius': 'var(--radius-md)',
    fontFamily: 'var(--font-family-base)',
} as CSSProperties;

export interface ToasterProps {
    /** Corner of the screen. @default 'top-right' */
    position?: SonnerToasterProps['position'];
    /** The app's current theme: sonner adjusts its icons and buttons to it. Colours come from the tokens. @default 'light' */
    theme?: SonnerToasterProps['theme'];
}

/**
 * Renders the toasts (sonner) in the theme colours: plain toasts on the page background, success / info / warning /
 * error on the soft colour of their role with a border in the role colour. Mount it once at the app root; show toasts
 * with `toast(…)`, `toast.success(…)`, `toast.error(…)` from `sonner`.
 *
 * @example
 * <Toaster />
 */
export function Toaster({ position = 'top-right', theme = 'light' }: ToasterProps): ReactElement {
    return <SonnerToaster position={position} theme={theme} richColors style={THEME_VARIABLES} />;
}
